import {
  mapProductDetailsToActivityDetails,
  normalizeProductDetailsData,
} from "@/lib/marketplace/marketplace-detail-utils";
import type { Support } from "@/types/api/booking-details";
import type {
  MarketplaceExperienceBookingDetails,
  MarketplaceExperienceBookingSupport,
} from "@/types/api/marketplace-experience-booking";
import type { ExperienceBookingDetails } from "@/types/api/experience-booking";

function timestampToIsoString(timestamp: number): string {
  if (!timestamp) return "";
  return new Date(timestamp).toISOString();
}

function nullableApiString(value: string | null | undefined): string {
  return value?.trim() ?? "";
}

function extractTimeFromDateTimeStr(
  dateTimeStr: string | null | undefined
): string {
  if (!dateTimeStr) return "";
  return dateTimeStr.trim().split(/\s+/)[1] ?? "";
}

function getOperatorName(
  support?: MarketplaceExperienceBookingSupport
): string {
  if (!support) return "";
  return (
    support["Operator Name"] ??
    Object.entries(support).find(
      ([key, value]) => key.toLowerCase().includes("operator") && value
    )?.[1] ??
    ""
  );
}

export function mapMarketplaceSupportToTripSupport(
  support?: MarketplaceExperienceBookingSupport
): Support | undefined {
  if (!support) return undefined;

  const operatorName = getOperatorName(support);
  const email = support.email ?? "";
  const phone = support.phone ?? "";

  if (!operatorName && !email && !phone) return undefined;

  return {
    operatorName: operatorName || undefined,
    pre_trip: {
      whatsapp: "",
      email,
      phone,
    },
    on_trip: {
      whatsapp: "",
      email,
      phone,
    },
  };
}

export function mapMarketplaceExperienceBookingToDetails(
  booking: MarketplaceExperienceBookingDetails
): ExperienceBookingDetails {
  const product = normalizeProductDetailsData(booking.product);
  const activityDetails = mapProductDetailsToActivityDetails(product);
  const coverPhoto =
    product.photos.find((photo) => photo.is_cover)?.url ??
    product.photos[0]?.url ??
    "/fallback/bokun_exp_fallback_img.webp";
  const tripSupport = mapMarketplaceSupportToTripSupport(booking.support);
  const priceDetails = booking.price;

  if (!activityDetails.photos.length) {
    activityDetails.photos = [coverPhoto];
  }

  return {
    uid: booking.bookingUid,
    inventoryBookingConfirmationCode: booking.confirmationCode,
    title: booking.title,
    status: booking.status,
    startTime: nullableApiString(booking.startTime),
    endTime: extractTimeFromDateTimeStr(booking.endDateTimeStr),
    startDateTime: nullableApiString(booking.startDateTimeStr),
    endDateTime: nullableApiString(booking.endDateTimeStr),
    date: nullableApiString(booking.dateStr),
    customer: {
      firstName: booking.customerDetails.firstName,
      lastName: booking.customerDetails.lastName,
      email: booking.customerDetails.email,
      phoneNumber: booking.customerDetails.phoneNumber,
    },
    price: {
      currency: priceDetails?.currency ?? booking.currency,
      totalAmount: priceDetails?.totalAmount ?? String(booking.totalPrice),
      totalAmountPaid:
        priceDetails?.totalAmountPaid ?? String(booking.totalPaid),
      totalAmountDue: priceDetails?.totalAmountDue ?? String(booking.totalDue),
      depositAmount: priceDetails?.depositAmount ?? "0",
      tcsAmount: priceDetails?.tcsAmount ?? "0",
      refundAmount: priceDetails?.refundAmount ?? "0",
      penaltyAmount: priceDetails?.penaltyAmount ?? "0",
      bookingPayments: priceDetails?.bookingPayments?.map((payment) => ({
        amount: payment.amount,
        currency: payment.currency,
        date: payment.date,
      })),
      bookingRefunds: priceDetails?.bookingRefunds?.map((refund) => ({
        amount: refund.amount,
        currency: refund.currency,
        date: refund.date,
        remarks: refund.remarks ?? "",
      })),
      bookingInstallments: priceDetails?.bookingInstallments,
    },
    pricingSummary: {
      line_items: booking.pricingSummary.lineItems.map((item) => ({
        title: item.title,
        currency: item.currency,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        total: item.total,
        totalDue: item.totalDue,
        pricingCategoryId: item.productPricingCategoryId,
      })),
    },
    cancellationPolicy:
      booking.cancellationPolicyFormatted.length > 0
        ? booking.cancellationPolicyFormatted
        : product.cancellationPolicies,
    passengers: booking.passengerDetails.map((passenger) => ({
      first_name: passenger.details.firstName ?? "",
      last_name: passenger.details.lastName ?? "",
      gender: null,
      dob: passenger.details.dateOfBirth ?? null,
      email: null,
      phone: null,
      documents: [],
    })),
    extras: [],
    creationDate: timestampToIsoString(booking.creationDate),
    language: booking.language,
    pickup: false,
    dropoff: false,
    pickupPlace: null,
    dropoffPlace: null,
    activityDetails,
    accommodationSet: booking.accommodationSet ?? null,
    quantityByPricingCategory: booking.passengerDetails.reduce(
      (acc, passenger) => {
        const key = String(passenger.productPricingCategoryId);
        acc[key] = (acc[key] ?? 0) + 1;
        return acc;
      },
      {} as Record<string, number>
    ),
    created_at: booking.created_at,
    updated_at: booking.updated_at,
    vouchers: (booking.vouchers ?? []).map((voucher) => ({
      type: voucher.type as ExperienceBookingDetails["vouchers"][number]["type"],
      name: voucher.name,
      url: voucher.url,
    })),
    support: {
      phone: Number.parseInt(tripSupport?.pre_trip.phone ?? "0", 10) || 0,
      email: tripSupport?.pre_trip.email ?? "",
      whatsApp: 0,
    },
  };
}
